import React from 'react';
import { Checkbox } from 'antd';
import {
  FmFormField,
  type FmBinaryFieldDescriptor,
  type FmFormFieldMetaProps,
} from './FmForm';

/**
 * FmCheckbox — DS-branded checkbox (wraps AntD Checkbox).
 *
 * SEALED API (muem9.28): the label is expressed as DATA, never as injected JSX.
 * There is no public `children: ReactNode` escape hatch (which let route code
 * smuggle arbitrary markup / raw controls into the label).
 *  - `text` — the simple string label.
 *  - `labelSegments` — a governed rich label for the terms/consent case that
 *    needs inline links. Each segment is plain text, or text + an `href` which
 *    the wrapper resolves to a DS-styled link INSIDE the wrapper. Route code
 *    passes the segment descriptors (data), never anchor JSX.
 */
export type FmCheckboxLabelSegment =
  | { text: string }
  | { text: string; href: string };

export interface FmCheckboxProps extends FmFormFieldMetaProps {
  descriptor?: FmBinaryFieldDescriptor;
  id?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  disabled?: boolean;
  /** Simple string label. */
  text?: string;
  /**
   * Tri-state selection flag for the bare (label-less) selection control —
   * e.g. a table select-all header. Only applies to the standalone path.
   */
  indeterminate?: boolean;
  /**
   * Governed rich label with inline links (no raw JSX). Resolved to DS text +
   * link elements INSIDE the wrapper. Use for terms/consent copy that embeds
   * links a plain string cannot represent.
   */
  labelSegments?: FmCheckboxLabelSegment[];
  /**
   * Accessible name for the bare selection control when there is no visible
   * text/label (e.g. row/select-all checkboxes in a governed table column).
   */
  ariaLabel?: string;
}

const toDescriptor = (props: FmCheckboxProps): FmBinaryFieldDescriptor => ({
  kind: 'binary',
  control: 'checkbox',
  name: props.name ?? '',
  label: String(props.label ?? props.text ?? ''),
  helper: props.helper ? String(props.helper) : undefined,
  error: props.error ? String(props.error) : undefined,
  required: props.required,
  basisNote: props.basisNote ? String(props.basisNote) : undefined,
  disabledReason: props.disabledReason ? String(props.disabledReason) : undefined,
  lockedReason: props.lockedReason ? String(props.lockedReason) : undefined,
  validation: props.validation,
  intent: props.intent,
  density: props.density,
  layoutPreset: props.layoutPreset,
  disabled: props.disabled,
  id: props.id,
  testId: props['data-testid'],
  checked: props.checked,
  defaultChecked: props.defaultChecked,
  onChange: props.onChange,
  text: props.text,
});

const renderLabel = (props: FmCheckboxProps): React.ReactNode => {
  if (props.labelSegments) {
    // Link segments resolve to a theme-styled anchor (link color comes from the
    // DS/AntD theme, not a page-local style) so route code never injects anchor
    // JSX or styling itself.
    return props.labelSegments.map((segment) =>
      'href' in segment ? (
        <a
          key={`${segment.href}:${segment.text}`}
          href={segment.href}
          target="_blank"
          rel="noreferrer"
          className="fm-checkbox__link"
        >
          {segment.text}
        </a>
      ) : (
        segment.text
      ),
    );
  }
  return props.text;
};

export const FmCheckbox: React.FC<FmCheckboxProps> = (props) => {
  if (props.descriptor) {
    return <FmFormField descriptor={props.descriptor} />;
  }

  // Rich (link-bearing) labels always take the bare render path so the governed
  // segments are resolved to DS text/link elements rather than a flat string.
  if (!props.labelSegments && props.name && (props.label || props.text)) {
    return <FmFormField descriptor={toDescriptor(props)} />;
  }

  return (
    <Checkbox
      id={props.id}
      checked={props.checked}
      defaultChecked={props.defaultChecked}
      indeterminate={props.indeterminate}
      onChange={(event) => props.onChange?.(event.target.checked)}
      disabled={props.disabled}
      aria-label={props.ariaLabel}
      data-testid={props['data-testid']}
      className="fm-checkbox"
    >
      {renderLabel(props)}
    </Checkbox>
  );
};

export default FmCheckbox;
